import { useEffect, useRef } from "react";
import { Box } from "@mui/material";
import L from "leaflet";
import {
  MapContainer,
  Marker,
  TileLayer,
  useMap,
  useMapEvents,
} from "react-leaflet";
import "leaflet/dist/leaflet.css";

/**
 * Leaflet's default marker icons are loaded from CSS that assumes the
 * `images/` folder ships next to the bundle. Vite + ESM bundlers don't
 * carry that over, so we point the prototype paths at the unpkg CDN
 * once at module load. Tiny, free, no API key.
 */
const DefaultIcon = L.icon({
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  iconRetinaUrl:
    "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});
L.Marker.prototype.options.icon = DefaultIcon;

interface Props {
  lat?: number;
  lng?: number;
  /** Called when the user clicks the map or drags the pin. */
  onPick?: (lat: number, lng: number) => void;
  /** Map height in px. Default 280. */
  height?: number;
}

const DEFAULT_CENTER: [number, number] = [59.3293, 18.0686]; // Stockholm

export default function PremiseMap({ lat, lng, onPick, height = 280 }: Props) {
  const center: [number, number] =
    typeof lat === "number" && typeof lng === "number"
      ? [lat, lng]
      : DEFAULT_CENTER;

  return (
    <Box
      sx={{
        width: "100%",
        height,
        borderRadius: 1,
        overflow: "hidden",
        border: 1,
        borderColor: "divider",
      }}
      data-testid="premise-map"
    >
      <MapContainer
        center={center}
        zoom={lat && lng ? 14 : 4}
        style={{ height: "100%", width: "100%" }}
        scrollWheelZoom
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        {typeof lat === "number" && typeof lng === "number" && (
          <Marker
            position={[lat, lng]}
            draggable={!!onPick}
            eventHandlers={{
              dragend: (e) => {
                const { lat: nLat, lng: nLng } = e.target.getLatLng();
                onPick?.(nLat, nLng);
              },
            }}
          />
        )}
        {onPick && <ClickToPick onPick={onPick} />}
        <Recenter lat={lat} lng={lng} />
      </MapContainer>
    </Box>
  );
}

function ClickToPick({
  onPick,
}: {
  onPick: (lat: number, lng: number) => void;
}) {
  useMapEvents({
    click: (e) => {
      onPick(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
}

/**
 * Keeps the map centred when the parent component receives new
 * lat/lng (e.g., after an address-search geocode). Without this the
 * map sits where it was first rendered.
 */
function Recenter({ lat, lng }: { lat?: number; lng?: number }) {
  const map = useMap();
  const last = useRef<{ lat?: number; lng?: number }>({});
  useEffect(() => {
    if (
      typeof lat === "number" &&
      typeof lng === "number" &&
      (last.current.lat !== lat || last.current.lng !== lng)
    ) {
      map.setView([lat, lng], Math.max(map.getZoom(), 13));
      last.current = { lat, lng };
    }
  }, [lat, lng, map]);
  return null;
}
